/**
 * Maps "canonical" channel ids (e.g. from broadcaster accessibility CSVs: bbc1.uk, itv1.uk)
 * to XMLTV feed channel id prefixes (e.g. BBCOneLondonHD.uk, ITV1London.uk).
 * Enables overlay rows with bbc1.uk to match programmes on BBCOneLondonHD.uk, BBCOneEast.uk, etc.
 */

/** Normalize for comparison: lowercase, strip .uk/.ie etc. */
function norm(s: string): string {
  return s.toLowerCase().trim().replace(/\.(uk|ie|sg|fr|qa|kr|ng)$/, '')
}

/** Pairs of (feed id prefix in lowercase, canonical ids that map to it). */
const FEED_PREFIX_TO_CANONICAL: { feedPrefix: string; canonicalIds: string[] }[] = [
  { feedPrefix: 'bbcone', canonicalIds: ['bbc1', 'bbc1.uk'] },
  { feedPrefix: 'bbctwo', canonicalIds: ['bbc2', 'bbc2.uk'] },
  { feedPrefix: 'bbcthree', canonicalIds: ['bbc3', 'bbc3.uk'] },
  { feedPrefix: 'bbcfour', canonicalIds: ['bbc4', 'bbc4.uk'] },
  { feedPrefix: 'bbcnews', canonicalIds: ['bbcnews', 'bbcnews.uk'] },
  { feedPrefix: 'cbbc', canonicalIds: ['cbbc', 'cbbc.uk'] },
  { feedPrefix: 'cbeebies', canonicalIds: ['cbeebies', 'cbeebies.uk'] },
  { feedPrefix: 'channel4', canonicalIds: ['channel4', 'channel4.uk', 'channel 4'] },
  { feedPrefix: 'e4', canonicalIds: ['e4', 'e4.uk'] },
  { feedPrefix: 'film4', canonicalIds: ['film4', 'film4.uk'] },
  { feedPrefix: 'more4', canonicalIds: ['more4', 'more4.uk'] },
  { feedPrefix: 'itv1', canonicalIds: ['itv1', 'itv1.uk', 'itv 1'] },
  { feedPrefix: 'itv2', canonicalIds: ['itv2', 'itv2.uk', 'itv 2'] },
  { feedPrefix: 'itv3', canonicalIds: ['itv3', 'itv3.uk', 'itv 3'] },
  { feedPrefix: 'itv4', canonicalIds: ['itv4', 'itv4.uk', 'itv 4'] },
]

/** Get canonical ids (e.g. bbc1.uk, bbc1) that map to this feed id. Used to include overlay rows in fetch. */
export function getCanonicalIdsForFeedId(feedId: string): string[] {
  const lower = feedId.toLowerCase()
  for (const { feedPrefix, canonicalIds } of FEED_PREFIX_TO_CANONICAL) {
    if (lower.startsWith(feedPrefix)) return canonicalIds
  }
  return []
}

/**
 * Expand overlay channel_id (from CSV) to all feed channel ids it matches.
 * e.g. bbc1.uk -> [BBCOneLondonHD.uk, BBCOneEast.uk, ...]
 * e.g. e4.uk -> [E4.uk, Channel4E4.uk, ...] (all feed prefixes whose canonical set includes e4).
 * If no alias matches, returns [overlayChannelId] for direct match.
 */
export function expandOverlayChannelToFeedIds(overlayChannelId: string, allFeedIds: string[]): string[] {
  const key = norm(overlayChannelId)
  if (!key) return [overlayChannelId]

  const matchingPrefixes = FEED_PREFIX_TO_CANONICAL
    .filter(({ canonicalIds }) => canonicalIds.some((c) => norm(c) === key))
    .map(({ feedPrefix }) => feedPrefix)

  if (matchingPrefixes.length === 0) return [overlayChannelId]

  const byPrefix = allFeedIds.filter((id) =>
    matchingPrefixes.some((prefix) => id.toLowerCase().startsWith(prefix))
  )
  if (byPrefix.length > 0) return byPrefix
  return allFeedIds.filter((id) => norm(id) === key)
}
